
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Webhooks from 'plaid-threads/Icons/Webhooks';
import Video from 'plaid-threads/Icons/Video';
import Table from '../../../../components/docs/Table';
import Layout from '../../../../components/docs/Layout';
import ContentSwitcher from '../../../../components/docs/ContentSwitcher';
export const metadata = {
  toc: true,
  source: 'webhook',
  layout: 'reference',
  description: 'API reference for webhooks, which allow you to receive programmatic updates when an Item goes into an error state or its account information is updated'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Webhooks' subtitle='API reference for webhooks' mdxType="Header" />
    <Callout icon={<Video mdxType="Video" />} mdxType="Callout">
      <p>{`Prefer to learn by watching? Our `}<a parentName="p" {...{
          "href": "https://www.youtube.com/watch?v=0E0KEAVeDyc"
        }}>{`video tutorial`}</a>{` walks you through the basics of incorporating Plaid webhooks into your application.`}</p>
    </Callout>
    <Callout mdxType="Callout">
      <p>{`Looking for webhook schemas? The reference documentation for specific webhooks (`}<a parentName="p" {...{
          "href": "/docs/api/products/transactions/#webhooks"
        }}>{`Transactions`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/api/products/auth/#webhooks"
        }}>{`Auth`}</a>{`,
`}<a parentName="p" {...{
          "href": "/docs/api/products/assets/#webhooks"
        }}>{`Assets`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/api/products/identity/#webhooks-beta"
        }}>{`Identity`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/api/products/identity-verification/#webhooks"
        }}>{`Identity Verification`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/api/products/monitor/"
        }}>{`Monitor`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/api/products/investments/#webhooks"
        }}>{`Investments`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/api/products/liabilities/#webhooks"
        }}>{`Liabilities`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/api/products/payment-initiation/#webhooks"
        }}>{`Payment Initiation`}</a>{`,
`}<a parentName="p" {...{
          "href": "/docs/api/products/income/#webhooks"
        }}>{`Income`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/api/products/virtual-accounts/#webhooks"
        }}>{`Virtual Accounts`}</a>{`, `}<a parentName="p" {...{
          "href": "/docs/api/items/#webhooks"
        }}>{`Items`}</a>{`, and `}<a parentName="p" {...{
          "href": "/docs/api/products/transfer"
        }}>{`Transfer`}</a>{`) has moved to its respective API reference pages.`}</p>
    </Callout>
    <h4 {...{
      "id": "introduction-to-webhooks"
    }}>{`Introduction to webhooks`}</h4>
    <p>{`A webhook is an HTTP request used to provide push notifications. Plaid sends webhooks to programmatically inform you about changes to Plaid Items or the status of asynchronous processes. For example, Plaid will send a webhook when an Item is in an error state or has additional data available, or when a non-blocking process (like gathering transaction data or verifying a bank account via micro-deposits) is complete.`}</p>
    <p>{`To receive Plaid webhooks, set up a dedicated endpoint on your server as a webhook listener that can receive POST requests, then provide this endpoint URL to Plaid as described in the next section. You can also test webhooks without setting up your own endpoint following the instructions in `}<a parentName="p" {...{
        "href": "#testing-webhooks-in-sandbox"
      }}>{`Testing webhooks in Sandbox`}</a>{`.`}</p>
    <h4 {...{
      "id": "configuring-webhooks"
    }}>{`Configuring webhooks`}</h4>
    <p>{`Webhooks are typically configured via the `}<inlineCode parentName="p">{`webhook`}</inlineCode>{` parameter of `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`, although some webhooks (especially those used in contexts where Link tokens are not always required), such as Identity Verification webhooks, are configured via the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/webhooks"
      }}>{`Plaid Dashboard`}</a>{` instead. When specifying a webhook, the URL must be in the standard format of `}<inlineCode parentName="p">{`http(s)://(www.)domain.com/`}</inlineCode>{` and, if https, must have a valid SSL certificate. `}</p>
    <p>{`To view response codes and debug any issues with webhook setup, see the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/activity/logs"
      }}>{`Logs section in the Dashboard`}</a>{`.`}</p>
    <p>{`Plaid sends POST payloads with raw JSON to your webhook URL from one of the following IP addresses:`}</p>
    <ul>
      <li parentName="ul">{`52.21.26.131`}</li>
      <li parentName="ul">{`52.21.47.157`}</li>
      <li parentName="ul">{`52.41.247.19`}</li>
      <li parentName="ul">{`52.88.82.239`}</li>
    </ul>
    <p>{`Note that these IP addresses are subject to change.`}</p>
    <p>{`You can optionally verify webhooks to ensure they are from Plaid. For more information, see `}<a parentName="p" {...{
        "href": "/docs/api/webhooks/webhook-verification"
      }}>{`webhook verification`}</a>{`.`}</p>
    <h4 {...{
      "id": "webhook-retries"
    }}>{`Webhook retries`}</h4>
    <p>{`If there is a non-2xx response or no response within 10 seconds from the webhook endpoint, Plaid will keep attempting to send the webhook for up to 24 hours. `}<inlineCode parentName="p">{`4xx`}</inlineCode>{` responses other than `}<inlineCode parentName="p">{`408`}</inlineCode>{`, `}<inlineCode parentName="p">{`425`}</inlineCode>{`, and `}<inlineCode parentName="p">{`429`}</inlineCode>{` are treated as permanent failures and are not retried. Each attempt will be tried after a delay that is 4 times longer than the previous delay, starting with 30 seconds.`}</p>
    <p>{`If your endpoint returns a `}<inlineCode parentName="p">{`429 Too Many Requests`}</inlineCode>{` response, Plaid will honor the `}<inlineCode parentName="p">{`Retry-After`}</inlineCode>{` header if present, waiting the specified duration for up to a maximum of 4 hours later before the next attempt instead of applying the standard exponential backoff. The `}<inlineCode parentName="p">{`Retry-After`}</inlineCode>{` value may be an integer number of seconds (e.g. `}<inlineCode parentName="p">{`120`}</inlineCode>{`), an HTTP date (e.g. `}<inlineCode parentName="p">{`Wed, 21 Oct 2026 07:28:00 GMT`}</inlineCode>{`), or an ISO 8601 timestamp (e.g. `}<inlineCode parentName="p">{`2026-10-21T07:28:00Z`}</inlineCode>{`).`}</p>
    <h4 {...{
      "id": "best-practices-for-applications-using-webhooks"
    }}>{`Best practices for applications using webhooks`}</h4>
    <p>{`You should design your application to handle duplicate and out-of-order webhooks. Ensure `}<a parentName="p" {...{
        "href": "https://martinfowler.com/articles/patterns-of-distributed-systems/idempotent-receiver.html"
      }}>{`idempotency`}</a>{` on actions you take when receiving a webhook. If you drive application state with webhooks, ensure your code doesn't rely on a specific order of webhook receipt.`}</p>
    <p>{`If you (or Plaid) experience downtime for longer than Plaid's `}<a parentName="p" {...{
        "href": "#webhook-retries"
      }}>{`retry period`}</a>{`, you will lose webhooks. Ensure your application can recover by implementing endpoint polling or other appropriate logic if a webhook is not received within an expected window. All data present in webhooks is also present in our other APIs. You can also use `}<inlineCode parentName="p">{`/beta/webhook_events/list`}</inlineCode>{` to retrieve the webhooks Plaid sent you over the previous 7 days and reconcile them against your own records.`}</p>
    <p>{`It's best to keep your receiver as simple as possible, such as a receiver whose only job is to write the webhook into a queue or reliable storage. This is important for two reasons. First, if the receiver does not respond within 10 seconds, the delivery is considered failed. Second, because webhooks can arrive at unpredictable rates. Therefore if you do a lot of work in your receiver - e.g. generating and sending an email - spikes are likely to overwhelm your downstream services, or cause you to be rate-limited if the downstream is a third-party.`}</p>
    <h4 {...{
      "id": "testing-webhooks-in-sandbox"
    }}>{`Testing webhooks in Sandbox`}</h4>
    <p>{`Webhooks will fire as normal in the Sandbox environment, with the exception of Transfer webhooks. For testing purposes, you can also use `}<inlineCode parentName="p">{`/sandbox/item/fire_webhook`}</inlineCode>{`, `}<inlineCode parentName="p">{`/sandbox/user/fire_webhook`}</inlineCode>{`, `}<inlineCode parentName="p">{`/sandbox/income/fire_webhook`}</inlineCode>{`, or `}<inlineCode parentName="p">{`/sandbox/transfer/fire_webhook`}</inlineCode>{` to fire a webhook on demand. If you don't have a webhook endpoint configured yet, you can also use a tool such as `}<a parentName="p" {...{
        "href": "https://webhook.site"
      }}>{`Webhook.site`}</a>{` or `}<a parentName="p" {...{
        "href": "https://requestbin.com/"
      }}>{`Request Bin`}</a>{` to quickly and easily set up a webhook listener endpoint. When directing webhook traffic to third-party tools, make sure you are using Plaid's Sandbox environment and not sending out live data.`}</p>
    <h4 {...{
      "id": "example-in-plaid-pattern"
    }}>{`Example in Plaid Pattern`}</h4>
    <p>{`For real-life examples of handling webhooks that illustrate how to handle sample transactions and Item webhooks, see `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern/blob/master/server/webhookHandlers/handleTransactionsWebhook.js"
      }}>{`handleTransactionsWebhook.js`}</a>{` and `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern/blob/master/server/webhookHandlers/handleItemWebhook.js"
      }}>{`handleItemWebhook.js`}</a>{` These files contain webhook handling code for the Node-based `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern"
      }}>{`Plaid Pattern`}</a>{` sample app.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "introduction-to-webhooks",
        level: 4,
        title: "Introduction to webhooks",
      },
{
        id: "configuring-webhooks",
        level: 4,
        title: "Configuring webhooks",
      },
{
        id: "webhook-retries",
        level: 4,
        title: "Webhook retries",
      },
{
        id: "best-practices-for-applications-using-webhooks",
        level: 4,
        title: "Best practices for applications using webhooks",
      },
{
        id: "testing-webhooks-in-sandbox",
        level: 4,
        title: "Testing webhooks in Sandbox",
      },
{
        id: "example-in-plaid-pattern",
        level: 4,
        title: "Example in Plaid Pattern",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
